import { Outlet, Link, useLocation } from 'react-router';
import { Cloud, Home, Camera, Heart, Settings, User, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { CookieConsentBanner } from './CookieConsentBanner';

export function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Stories', href: '/stories', icon: Cloud },
    { name: 'Cloud ID', href: '/cloud', icon: Camera },
    { name: 'Bookmarks', href: '/bookmarks', icon: Heart },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <Cloud className="h-8 w-8 text-primary-500" />
                <span className="font-display text-xl font-bold text-gray-100">
                  Weathr Story
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-primary-400'
                      : 'text-gray-400 hover:text-gray-100'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            {/* User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/settings"
                    className="text-gray-400 hover:text-gray-100"
                  >
                    <Settings className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 text-sm font-medium text-gray-300"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                      {user?.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.displayName || 'User'}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="h-4 w-4 text-primary-400" />
                      )}
                    </div>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost">
                    Sign in
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-400 hover:text-gray-100"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-700">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                    isActive(item.href)
                      ? 'bg-gray-700 text-primary-400'
                      : 'text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              ))}
              <hr className="my-2 border-gray-700" />
              {isAuthenticated ? (
                <>
                  <Link
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-700"
                  >
                    <Settings className="h-5 w-5" />
                    <span>Settings</span>
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-700"
                  >
                    <User className="h-5 w-5" />
                    <span>Profile</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-sm font-medium text-gray-400"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 btn-primary text-center"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <Cloud className="h-6 w-6 text-primary-500" />
              <span className="font-display font-bold text-gray-100">
                Weathr Story
              </span>
            </div>
            <p className="text-sm text-gray-400">
              Making weather worth talking about.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-gray-100">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-100">Terms</Link>
              <Link to="/contact" className="hover:text-gray-100">Contact</Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center mb-3">
              Weather data from{' '}
              <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">Open-Meteo</a>
              . Historical data from{' '}
              <a href="https://www.longpaddock.qld.gov.au/silo/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">SILO</a>
              {' '}(Queensland Government, CC BY 4.0).
            </p>
            <p className="text-sm text-gray-500 text-center">
              © 2026 <a href="https://marwood.au" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">Marwood</a>. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <CookieConsentBanner />
    </div>
  );
}
